import "../styles.scss";

import { API, Endpoints } from "utils/api";
import { Col, Input, Modal, Popconfirm, Row, notification } from "antd";
import { push, replace } from "connected-react-router";
import {
	setAcceptedChat,
	setBreadcrumb,
	setFinishedChat,
	setLoader,
	setPendingMessages,
	setToDelete
} from "store/actions";

import Animation from "assets/images/lottie/chat.json";
import { DateTime } from "luxon";
import DoctorPlaceholder from "assets/images/placeholders/doctor.jpg";
import { Footer } from "screens";
import { Helmet } from "react-helmet";
import { Icons } from "components";
import Lottie from "lottie-web";
import NoSelection from "assets/images/illustrations/team.png";
import React from "react";
import Strings from "utils/strings";
import UserPlaceholder from "assets/images/placeholders/user2x.jpg";
// @ts-ignore
import Variables from "styles/variables.scss";
import { connect } from "react-redux";

// Memoized component for individual messages to prevent re-renders
const MessageItem = React.memo(
	({ message, user, chat, lastMessageSameUser, dayChanged, deleteMessage, setEditMessage }: any) => {
		const rightSide = message.user?._id === user._id;

		return (
			<React.Fragment>
				{dayChanged && (
					<div className="ChatMessageNewDay">
						{DateTime.fromISO(message.createdAt).toFormat("dd/MM/yyyy")}
					</div>
				)}
				<Row>
					{rightSide && (
						<Col xs={{ span: 16, offset: 8 }}>
							<div className="ChatAlignRight">
								{!lastMessageSameUser && (
									<div className="ChatUserBlock --chat-align-right">
										<div className="ChatUserAvatar">
											<img src={user.photo || DoctorPlaceholder} alt="user avatar" />
										</div>
										<div className="ChatUserDetails">
											<p>{message.user?.externalName || message.user?.name}</p>
											<small>{DateTime.fromISO(message.createdAt).toFormat("HH:mm")}</small>
										</div>
									</div>
								)}
								<div
									className={`ChatEntry ${rightSide ? "--chat-me" : ""} ${
										chat?.finished ? "--finished" : ""
									}`}
								>
									<span className={"message"}>{message.text}</span>
									<div className="ChatEntryFooter">
										<span
											className={`checkdouble-icon ${
												message.read ? "--message-read" : "--message-unread"
											}`}
										>
											<Icons.CheckDouble />
										</span>
										{!chat?.finished && (
											<>
												<span
													className="pencil-icon"
													onClick={() => {
														setEditMessage(message);
													}}
												>
													<Icons.Pencil color={"#ffffff"} />
												</span>
												<Popconfirm
													placement="topRight"
													title={Strings.chat.confirmDeleteMessage}
													onConfirm={() => {
														deleteMessage(message._id);
													}}
													okText={Strings.generic.yes}
													cancelText={Strings.generic.no}
												>
													<span className="bin-icon">
														<Icons.Bin color={"white"} />
													</span>
												</Popconfirm>
											</>
										)}
									</div>
								</div>
							</div>
						</Col>
					)}
					{!rightSide && (
						<Col xs={16}>
							<div>
								{!lastMessageSameUser && (
									<div className="ChatUserBlock">
										<div className="ChatUserAvatar">
											<img src={message.user?.avatar || UserPlaceholder} alt="user avatar" />
										</div>
										<div className="ChatUserDetails">
											<p>{message.user?.externalName || message.user?.name}</p>
											<small>{DateTime.fromISO(message.createdAt).toFormat("HH:mm")}</small>
										</div>
									</div>
								)}
								<span className="ChatEntry">{message.text}</span>
							</div>
						</Col>
					)}
				</Row>
			</React.Fragment>
		);
	},
	(prevProps, nextProps) => {
		// Return true if props are equal (don't re-render)
		return (
			prevProps.message._id === nextProps.message._id &&
			prevProps.message.text === nextProps.message.text &&
			prevProps.message.read === nextProps.message.read &&
			prevProps.lastMessageSameUser === nextProps.lastMessageSameUser &&
			prevProps.dayChanged === nextProps.dayChanged &&
			prevProps.chat?.finished === nextProps.chat?.finished &&
			prevProps.user._id === nextProps.user._id
		);
	}
);

MessageItem.displayName = "MessageItem";

const MessageList = React.memo(
	({
		messages,
		user,
		chat,
		chatContent,
		deleteMessage,
		setEditMessage,
		selected,
		onStartChat,
		onScroll,
		loadingMoreMessages
	}: any) => {
		return (
			<div id="chatContent" ref={chatContent} className="ChatMessagesContainer" onScroll={onScroll}>
				{loadingMoreMessages && (
					<div className="ChatLoadingMoreMessages">
						<span></span>
					</div>
				)}
				{messages?.map((message: any, index: number) => {
					let lastMessageSameUser = false;
					let dayChanged = false;

					if (index > 0) {
						lastMessageSameUser =
							messages[index - 1].user?._id === message.user?._id &&
							DateTime.fromISO(messages[index - 1].createdAt).toFormat("HH:mm") ===
								DateTime.fromISO(message.createdAt).toFormat("HH:mm");

						if (
							DateTime.fromISO(message.createdAt) > DateTime.fromISO(messages[index - 1].createdAt) &&
							DateTime.fromISO(message.createdAt).day !==
								DateTime.fromISO(messages[index - 1].createdAt).day
						) {
							dayChanged = true;
						}
					} else {
						dayChanged = true; // Show date for first message
					}

					return (
						<MessageItem
							key={`chat_message_${message._id}`}
							message={message}
							user={user}
							chat={chat}
							lastMessageSameUser={lastMessageSameUser}
							dayChanged={dayChanged}
							deleteMessage={deleteMessage}
							setEditMessage={setEditMessage}
						/>
					);
				})}
				{Boolean(chat?.finished) && (
					<div className="ChatClosedOuterBlock">
						<div className="ChatClosedInnerBlock">
							<p>
								{Strings.chat.closedChatFormat1}{" "}
								<span onClick={() => onStartChat?.(chat?.user)}>{Strings.chat.here}</span>{" "}
								{Strings.chat.closedChatFormat2}
							</p>
						</div>
					</div>
				)}
				{(!selected && (
					<div className="ChatNoSelection">
						<div className="ChatNoSelectionIllustration">
							<img src={NoSelection} alt="chat illustration" />
						</div>
						<p>{Strings.chat.selectChatPsych}</p>
					</div>
				)) ||
					null}
			</div>
		);
	}
);

MessageList.displayName = "MessageList";

const arePropsEqual = (prevProps: any, nextProps: any) => {
	// Custom comparison function - return true if props are equal (don't re-render)
	// Messages is the most important prop to compare
	if (prevProps.messages !== nextProps.messages) {
		// Check if the arrays have the same length and same IDs
		if (prevProps.messages?.length !== nextProps.messages?.length) {
			return false;
		}
		// For same length, compare ID and text of every message to catch edits
		if (prevProps.messages?.length > 0) {
			for (let i = 0; i < prevProps.messages.length; i++) {
				if (
					prevProps.messages[i]?._id !== nextProps.messages[i]?._id ||
					prevProps.messages[i]?.text !== nextProps.messages[i]?.text
				) {
					return false;
				}
			}
		}
	}

	// Compare other important props
	if (prevProps.selected !== nextProps.selected) return false;
	if (prevProps.chat !== nextProps.chat) return false;
	if (prevProps.user?._id !== nextProps.user?._id) return false;
	if (prevProps.loadingMoreMessages !== nextProps.loadingMoreMessages) return false;

	return true;
};

export const MemoizedMessageList = React.memo(MessageList, arePropsEqual);

class Chat extends React.Component<any, any> {
	chatContent: any;
	ref: any;
	input: any;
	intervalId: any;
	isInitialLoad: boolean;
	isScrollingToBottom: boolean;
	searchTimer: any;

	constructor(props: any) {
		super(props);

		this.intervalId = null;
		this.isInitialLoad = false;

		this.searchTimer = null;
		this.isScrollingToBottom = false;

		this.state = {
			chats: [],
			messages: [],
			selected: null,
			showEditMessageModal: false,
			editMessage: "",
			showEndModal: false,
			showPatientModal: false,
			gettingMessages: true,
			search: "",
			inputMessage: "",
			chatsPage: 1,
			chatsHasMore: false,
			loadingMoreChats: false,
			messagesPage: 1,
			messagesHasMore: false,
			loadingMoreMessages: false
		};

		this.input = React.createRef();

		// Bind methods to prevent new function instances on each render
		this.handleDeleteMessage = this.handleDeleteMessage.bind(this);
		this.handleSetEditMessage = this.handleSetEditMessage.bind(this);
		this.handleStartChat = this.handleStartChat.bind(this);
		this.handleSetChatContentRef = this.handleSetChatContentRef.bind(this);
	}

	handleDeleteMessage(id: string) {
		this.deleteMessage(id);
	}

	handleSetEditMessage(message: any) {
		this.setState({
			showEditMessageModal: true,
			editMessage: message
		});
	}

	handleStartChat(user: any) {
		this.startChat(user);
	}

	handleSetChatContentRef(ref: any) {
		this.chatContent = ref;
	}

	async componentDidMount() {
		const { dispatch, location } = this.props;
		const { state } = location;
		const { chatId } = state || {};

		dispatch(
			setBreadcrumb(() => ({
				locations: [
					{
						text: Strings.chat.header,
						route: "/chat"
					}
				]
			}))
		);

		if (this.chatContent) {
			setTimeout(() => {
				this.chatContent.scrollTop = this.chatContent.scrollHeight;
			}, 100);
		}

		this.getData({ selectChat: chatId || "" });

		this.intervalId = setInterval(() => {
			if (document.visibilityState === "visible" && this.state.selected && this.state.messagesPage === 1) {
				this.getMessages(this.state.selected, false, 1);
			}
		}, 5000);
	}

	UNSAFE_componentWillReceiveProps(nextProps: any) {
		const { dispatch } = this.props;
		const { location } = nextProps;
		const { state } = location;
		const { chatId } = state || {};

		if (chatId) {
			this.getData({ selectChat: chatId });
			dispatch(replace("/chat"));
		}
	}

	componentDidUpdate(prevProps: any, prevState: any) {
		const { selected, chats } = this.state;
		const { pendingMessages, acceptedChat, finishedChat, toDelete, dispatch } = this.props;

		if (
			pendingMessages.pending &&
			!prevProps.pendingMessages?.pending &&
			Boolean(selected) &&
			selected === pendingMessages?.id
		) {
			this.getMessages(pendingMessages.id, true, 1);
		}

		if (pendingMessages.pending && pendingMessages.id !== selected) {
			const chat = chats.find((tmp: any) => tmp._id === pendingMessages.id);

			if (chat) {
				chat.pendingMessages = true;
				chat.lastMessage = {
					message: pendingMessages.message,
					_created: DateTime.utc()
				};
			}

			this.setState({ chats });
			dispatch(
				setPendingMessages({
					pending: false,
					id: null,
					message: "",
					data: null
				})
			);
		}

		if (acceptedChat?.accepted) {
			const chats = this.state.chats || [];
			const chat = chats.find((tmp: any) => tmp._id === acceptedChat?.current);

			if (chat) {
				this.getData({ selectChat: acceptedChat?.record });
			}

			dispatch(
				setAcceptedChat({
					accepted: false,
					current: null,
					record: null
				})
			);
		}

		if (finishedChat?.finished) {
			const chats = this.state.chats || [];
			const chat = chats.find((tmp: any) => tmp._id === finishedChat?.record);

			if (chat) {
				this.getData({ selectChat: finishedChat?.record });
			}

			dispatch(setFinishedChat({ finished: false, record: null }));
		}

		if (toDelete?.delete) {
			const chats = this.state.chats || [];
			const chat = chats.find((tmp: any) => tmp._id === toDelete?.record);

			if (chat) {
				this.getData({ toDelete: true });
			}

			dispatch(setToDelete({ finished: false, record: null }));
		}
	}

	componentWillUnmount() {
		if (this.intervalId) {
			clearInterval(this.intervalId); // Clear the interval when the component is unmounted
		}

		if (this.searchTimer) {
			clearTimeout(this.searchTimer);
		}
	}

	async getData({
		selectChat,
		toDelete = false,
		silent = false,
		page = 1
	}: {
		newChat?: boolean;
		selectChat?: string;
		silent?: boolean;
		toDelete?: boolean;
		page?: number;
	}) {
		const { dispatch } = this.props;

		if (!silent) {
			dispatch(setLoader(true));
		}

		let response: any;
		try {
			response = await API.get({
				url: Endpoints.uriChats(),
				params: { page, limit: 20, q: this.state.search }
			});

			if (response.ok) {
				const { chats: newChats, hasMore } = response.data.results || {};

				if (page === 1) {
					if (!newChats.length) {
						this.loadAnimation();
					} else {
						Lottie.destroy();
					}

					let selected = null;

					if (selectChat && newChats.length) {
						selected = selectChat;
					}

					if (toDelete) {
						selected = null;
					}

					this.setState({ chats: newChats, selected, chatsPage: 1, chatsHasMore: hasMore || false }, () => {
						if (selectChat) {
							this.isInitialLoad = true;
							this.getMessages(selectChat, true, 1);
						}
					});
				} else {
					this.setState((prevState: any) => ({
						chats: [...prevState.chats, ...newChats],
						chatsPage: page,
						chatsHasMore: hasMore || false,
						loadingMoreChats: false
					}));
				}
			} else {
				notification.error({
					message: Strings.sidebar.chat,
					description: response?.data?.message || Strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
				if (page > 1) this.setState({ loadingMoreChats: false });
			}
		} catch (err) {
			notification.error({
				message: Strings.sidebar.chat,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
			if (page > 1) this.setState({ loadingMoreChats: false });
		}

		if (!silent) {
			dispatch(setLoader(false));
		}
	}

	async getMessages(id: string, refresh: boolean = true, page: number = 1) {
		const { chats } = this.state;
		const { dispatch } = this.props;

		const chat = chats.find((tmp: any) => tmp._id === id);
		if (chat) {
			chat.pendingMessages = false;
		}

		if (page === 1) {
			if (refresh) this.setState({ gettingMessages: true, chats });
			else this.setState({ chats });
		} else {
			this.setState({ loadingMoreMessages: true });
		}

		let response: any;
		try {
			response = await API.get({
				url: Endpoints.uriChats(id),
				params: { page, limit: 30 }
			});

			if (response.ok) {
				const { messages: newMessages, hasMore } = response.data.results || {};

				if (page === 1) {
					if (chat) {
						chat.lastMessage = {
							message: newMessages[newMessages.length - 1]?.text,
							_created: newMessages[newMessages.length - 1]?.createdAt
						};
					}

					const initialLoad = this.isInitialLoad;
					this.isInitialLoad = false;

					if (this.chatContent && refresh && initialLoad) {
						this.chatContent.style.visibility = "hidden";
					}

					this.setState(
						{ messages: newMessages, messagesPage: 1, messagesHasMore: hasMore || false, chats },
						() => {
							if (this.chatContent && refresh) {
								this.isScrollingToBottom = true;
								setTimeout(() => {
									this.chatContent.style.scrollBehavior = "auto";
									this.chatContent.scrollTop = 999999;
									requestAnimationFrame(() => {
										this.chatContent.style.visibility = "visible";
										this.chatContent.style.scrollBehavior = "smooth";
										this.isScrollingToBottom = false;
									});
								}, 100);
							}
						}
					);
				} else {
					const prevScrollHeight = this.chatContent?.scrollHeight || 0;
					const prevScrollTop = this.chatContent?.scrollTop || 0;
					this.setState(
						(prevState: any) => ({
							messages: [...newMessages, ...prevState.messages],
							messagesPage: page,
							messagesHasMore: hasMore || false,
							loadingMoreMessages: false
						}),
						() => {
							if (this.chatContent) {
								const prevScrollBehavior = this.chatContent.style.scrollBehavior;
								this.chatContent.style.scrollBehavior = "smooth";
								this.chatContent.scrollTop =
									prevScrollTop + (this.chatContent.scrollHeight - prevScrollHeight);
								this.chatContent.style.scrollBehavior = prevScrollBehavior;
							}
						}
					);
				}

				dispatch(setPendingMessages({ pending: false, id: null, message: "", data: null }));
			} else {
				notification.error({
					message: Strings.sidebar.chat,
					description: response?.data?.message || Strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
				if (page > 1) this.setState({ loadingMoreMessages: false });
			}
		} catch (err) {
			notification.error({
				message: Strings.sidebar.chat,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
			if (page > 1) this.setState({ loadingMoreMessages: false });
		}

		if (page === 1) {
			this.setState({ gettingMessages: false });
		}
	}

	async deleteMessage(id: string) {
		let response: any;

		try {
			response = await API.delete({
				url: Endpoints.uriChatMessage(this.state.selected, id)
			});

			if (response.ok) {
				this.getMessages(this.state.selected, false, 1);
			}
		} catch (err) {
			notification.error({
				message: Strings.sidebar.chat,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}

		this.setState({ gettingMessages: false });
	}

	async editMessage() {
		let response: any;
		let body = {
			message: this.state.editMessage.text
		} as any;

		try {
			response = await API.put({
				url: Endpoints.uriChatMessage(this.state.selected, this.state.editMessage._id),
				data: body
			});

			if (response.ok) {
				this.getMessages(this.state.selected, false, 1);

				this.setState({
					showEditMessageModal: false,
					editMessage: null
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.sidebar.chat,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}

		this.setState({ gettingMessages: false });
	}

	async sendMessage() {
		const { selected, inputMessage, messages } = this.state;
		const { user } = this.props;

		if (!selected || !inputMessage) return;

		// Optimistically append the message immediately
		const tempId = `temp_${Date.now()}`;
		const optimisticMessage = {
			_id: tempId,
			text: inputMessage,
			user: { _id: user._id, name: user.name, photo: user.photo },
			createdAt: new Date().toISOString(),
			read: false
		};

		this.setState({ inputMessage: "", messages: [...messages, optimisticMessage] }, () => {
			if (this.chatContent) {
				this.chatContent.style.scrollBehavior = "smooth";
				this.chatContent.scrollTop = this.chatContent.scrollHeight;
			}
			this.input?.current?.focus();
		});

		let response: any;
		try {
			response = await API.post({
				url: Endpoints.uriChats(selected),
				data: {
					message: inputMessage
				}
			});

			if (response.ok) {
				await this.getMessages(selected, true, 1);
			} else {
				// Roll back optimistic message
				this.setState({ messages, inputMessage });
				notification.error({
					message: Strings.sidebar.chat,
					description: response?.data?.message || Strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			// Roll back optimistic message
			this.setState({ messages, inputMessage });
			notification.error({
				message: Strings.sidebar.chat,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	async startChat(user: any) {
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		let response: any;
		try {
			let body = {
				user: user._id
			} as any;

			response = await API.patch({
				url: Endpoints.uriChats("start"),
				data: body
			});

			if (response.ok) {
				this.getData({ selectChat: response.data.results.chat?.socketId });
			} else {
				notification.error({
					message: Strings.sidebar.chat,
					description: response?.data?.message || Strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.sidebar.chat,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async closeChat() {
		const { selected } = this.state;

		if (!selected) return;

		let response: any;
		try {
			response = await API.patch({
				url: Endpoints.uriChats(`finish/${selected}`),
				data: {}
			});

			if (response.ok) {
				this.setState({
					showEndModal: false,
					selected: null,
					messages: []
				});
			} else {
				notification.error({
					message: Strings.sidebar.chat,
					description: response?.data?.message || Strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.sidebar.chat,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	loadAnimation() {
		Lottie.loadAnimation({
			container: this.ref,
			renderer: "svg",
			loop: true,
			autoplay: true,
			animationData: Animation
		});
	}

	renderEndChatModal() {
		const { showEndModal } = this.state;
		const { mobile } = this.props;

		return (
			<Modal
				centered={true}
				closable={true}
				onCancel={() => this.setState({ showEndModal: false })}
				visible={showEndModal}
				footer={null}
				width={mobile ? "100%" : 400}
			>
				<div className="EndChatModal">
					<h2>{Strings.chat.endChat}</h2>
					<div className="EndChatOptions">
						<button onClick={() => this.setState({ showEndModal: false })}>{Strings.generic.no}</button>
						<button
							onClick={() => {
								this.closeChat();
								// this.getData({ silent: true });
							}}
							className="--btn-primary"
						>
							{Strings.generic.yes}
						</button>
					</div>
				</div>
			</Modal>
		);
	}

	renderEditMessageModal() {
		const { showEditMessageModal } = this.state;
		const { mobile } = this.props;

		return (
			<Modal
				centered={true}
				closable={true}
				onCancel={() => this.setState({ showEditMessageModal: false })}
				visible={showEditMessageModal}
				footer={null}
				width={mobile ? "100%" : 400}
			>
				<div className="EndChatModal">
					<h2>{Strings.fields.edit}</h2>
					<div>
						<Input
							value={this.state.editMessage?.text || ""}
							onChange={(e) =>
								this.setState({
									editMessage: {
										...this.state.editMessage,
										text: e.target.value
									}
								})
							}
						/>
					</div>
					<div className="EndChatOptions">
						<button onClick={() => this.setState({ showEditMessageModal: false })}>
							{Strings.appointments.cancel}
						</button>
						<button
							onClick={() => {
								this.editMessage();
							}}
							className="--btn-primary"
						>
							{Strings.generic.confirm}
						</button>
					</div>
				</div>
			</Modal>
		);
	}

	handleChatListScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const { loadingMoreChats, chatsHasMore, chatsPage } = this.state;
		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - scrollTop - clientHeight < 60 && chatsHasMore && !loadingMoreChats) {
			this.setState({ loadingMoreChats: true });
			this.getData({ page: chatsPage + 1, silent: true });
		}
	};

	handleMessagesScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const { loadingMoreMessages, messagesHasMore, messagesPage, selected } = this.state;
		const { scrollTop } = e.currentTarget;

		if (scrollTop < 60 && messagesHasMore && !loadingMoreMessages && selected && !this.isScrollingToBottom) {
			this.getMessages(selected, false, messagesPage + 1);
		}
	};

	getFilteredChats = (chats: any) => {
		const { search } = this.state;

		// Quick return if no search
		if (!search) {
			return chats;
		}

		const normalizeString = (str: string) => {
			return str
				.normalize("NFD")
				.replace(/[\u0300-\u036f]/g, "")
				.toLowerCase();
		};

		const normalizedSearch = normalizeString(search);

		return chats.filter((chat: any) => {
			const userName = chat.user?.name || "";
			const userExternalName = chat.user?.externalName || "";
			const normalizedUserName = normalizeString(userName);
			const normalizedUserExternalName = normalizeString(userExternalName);

			return (
				normalizedUserName.includes(normalizedSearch) || normalizedUserExternalName.includes(normalizedSearch)
			);
		});
	};

	render() {
		const { chats, selected, inputMessage, messages, loadingMoreChats, loadingMoreMessages } = this.state;
		const { user, mobile, dispatch } = this.props;

		let chat: any;
		if (chats.length && selected) {
			chat = chats.find((chat: any) => chat._id === selected);
		}

		return (
			<React.Fragment>
				<Helmet>
					<title>{Strings.sidebar.chat} | Team24</title>
					<meta name="description" content="Description of Chat" />
				</Helmet>
				<div className="ChatContent">
					<h1 className="GenericTitle">{Strings.sidebar.chat}</h1>
					{chats?.length > 0 ? (
						<div className="ChatBoxWrapper">
							<div className="ChatUserListContainer">
								<div className="ChatUserListHeader">
									<Input.Search
										value={this.state.search || ""}
										placeholder={Strings.generic.search}
										onChange={(e: any) => {
											const val = e.target.value;
											this.setState({ search: val });
											if (this.searchTimer) clearTimeout(this.searchTimer);
											this.searchTimer = setTimeout(() => this.getData({ page: 1, silent: true }), 300);
										}}
									/>
								</div>
								<div className="ChatUserList" onScroll={this.handleChatListScroll}>
									{chats.map((chat: any, index: number) => (
										<div
											key={`chatUser_${chat._id}`}
											className={`ChatUser${selected === chat._id ? " --chat-selected" : ""}`}
											onClick={() => {
												if (selected !== chat._id) {
													this.setState(
														{
															selected: chat._id,
															messages: [],
															messagesPage: 1,
															messagesHasMore: false
														},
														() => {
															this.isInitialLoad = true;
															this.getMessages(chat._id, true, 1);
														}
													);
												}
											}}
										>
											<img src={chat.user?.photo || UserPlaceholder} alt="chatUser avatar" />
											<div className="ChatUserDetails">
												<div className="ChatUserRow">
													<p>{chat.user?.name}</p>
													{Boolean(chat?.lastMessage?._created) ? (
														<p className="ChatMessageTime">
															{DateTime.fromISO(chat.lastMessage?._created).toFormat(
																"HH:mm"
															)}
														</p>
													) : (
														<p className="ChatMessageTime">
															{DateTime.fromISO(chat.startDate).toFormat("HH:mm")}
														</p>
													)}
												</div>
												{((chat.finished || Boolean(chat?.lastMessage?.message)) && (
													<div className="ChatUserRow">
														{Boolean(chat?.lastMessage?.message) ? (
															<p
																className={`ChatMessage${
																	chat?.pendingMessages ? " --bold" : ""
																}`}
															>
																{chat?.lastMessage?.message}
															</p>
														) : (
															<span>&nbsp;</span>
														)}
														{chat.finished && (
															<div className="ChatClosedBadge">
																{Strings.fields.closed}
															</div>
														)}
													</div>
												)) ||
													null}
											</div>
										</div>
									))}
									{loadingMoreChats && (
										<div className="ChatLoadingMore">
											<span></span>
										</div>
									)}
								</div>
							</div>
							<div className="ChatConversationContainer">
								{(selected && (
									<div className="ChatSelectedUser">
										<div
											onClick={() => dispatch(push(`/patients/${chat?.user?._id}`))}
											className="ChatSelectedUserDetail"
										>
											<img src={chat?.user?.photo || UserPlaceholder} alt="psychologist avatar" />
											<p>{chat?.user?.externalName || chat?.user?.name}</p>
										</div>
										<button
											disabled={chat?.finished}
											onClick={() => this.setState({ showEndModal: true })}
										>
											<em className="moon-close" />
										</button>
									</div>
								)) ||
									null}
								<MemoizedMessageList
									messages={messages}
									user={user}
									chat={chat}
									selected={selected}
									chatContent={this.handleSetChatContentRef}
									deleteMessage={this.handleDeleteMessage}
									setEditMessage={this.handleSetEditMessage}
									onStartChat={this.handleStartChat}
									onScroll={this.handleMessagesScroll}
									loadingMoreMessages={loadingMoreMessages}
								/>
								{(selected && (
									<div className="ChatSendMessageContainer">
										<textarea
											ref={this.input}
											rows={2}
											value={inputMessage || ""}
											onChange={(e: any) =>
												this.setState({
													inputMessage: e.target.value
												})
											}
											placeholder={Strings.appointments.replyMessage}
											disabled={chat?.finished}
											onKeyPress={(e: any) => {
												if (e.key === "Enter" && !e.shiftKey) {
													e.preventDefault();

													if (inputMessage?.trim()) {
														this.sendMessage();
													}

													return;
												}
											}}
										/>
										<button
											className="ChatSendButton"
											onClick={() => {
												if (inputMessage?.trim()) {
													this.sendMessage();
												}
											}}
											disabled={chat?.finished}
										>
											<Icons.Send disabled={!inputMessage} />
										</button>
									</div>
								)) ||
									null}
							</div>
						</div>
					) : (
						<div className="EmptyChats">
							<div
								ref={(ref: any) => {
									this.ref = ref;
								}}
							/>
							<p>{Strings.chat.noChats}</p>
						</div>
					)}
				</div>
				{mobile && <Footer />}
				{this.renderEndChatModal()}
				{this.renderEditMessageModal()}
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	user: state.user,
	mobile: state.mobile,
	pendingMessages: state.pendingMessages,
	acceptedChat: state.acceptedChat,
	finishedChat: state.finishedChat,
	toDelete: state.toDelete
});

export default connect(mapStateToProps)(Chat);
